import React, { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { AppCredentialsManager } from '../components/AppCredentialsManager';
import { CorsManager } from '../components/CorsManager';
import { WebhookManager } from '../components/webhooks/WebhookManager';
import { QuotaAlertSettings } from '../components/QuotaAlertSettings';
import { ApiKeyDiagnosticModal } from '../components/ApiKeyDiagnosticModal';
import { CodeSnippetGenerator } from '../components/CodeSnippetGenerator';
import {
  PlusCircle,
  Key,
  Shield,
  Layers,
  Calendar,
  Server,
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Gauge,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import type { Application, Subscription, Product } from '../types';

interface ContextType {
  application: Application;
}

interface SubscriptionQuotaItem {
  subscription_id: string;
  product_name: string;
  tier: string;
  monthly_quota: number;
  current_month_usage: number;
  remaining_quota: number;
  quota_percentage: number;
  rate_limit_per_minute: number;
  monthly_price: number;
}

interface AnalyticsData {
  summary: {
    total_requests: number;
    successful_requests: number;
    client_errors: number;
    server_errors: number;
    average_response_time: number;
    total_cost: number;
    success_rate: number;
    error_rate: number;
  };
  subscriptions_quota: SubscriptionQuotaItem[];
}

export const ApplicationOverview: React.FC = () => {
  const { application } = useOutletContext<ContextType>();
  const { client } = useAuthStore();
  const [extraProductIds, setExtraProductIds] = useState<string[]>([]);
  const [selectedToAdd, setSelectedToAdd] = useState<string>('');

  const gatewayUrl =
    (import.meta as any).env?.VITE_KONG_GATEWAY_URL ||
    (import.meta as any).env?.VITE_API_BASE_URL ||
    (typeof window !== 'undefined' && window.location.origin.includes('tatusolutions.com')
      ? 'https://api-gateway.tatusolutions.com'
      : 'http://localhost:8000');

  const { data: apiKeyData } = useQuery({
    queryKey: ['application-api-key', client?.id, application.id],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: { api_key: string } }>(
          `/clients/${client?.id}/applications/${application.id}/api-key`
        );
        return res.data?.data?.api_key || null;
      } catch {
        return null;
      }
    },
    enabled: !!client?.id && !!application.id,
    staleTime: 60000,
  });

  const { data: subsData } = useQuery<{ data: Subscription[] }>({
    queryKey: [
      'applications-subscriptions',
      application.sandbox_mode ? 'sandbox' : 'production',
      client?.id,
    ],
    queryFn: async () => {
      const response = await api.get(`/clients/${client?.id}/subscriptions?limit=100`);
      return response.data;
    },
    enabled: !!client?.id,
  });

  const { data: productsData } = useQuery<{ data: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.get('/products');
      return response.data;
    },
  });

  // Fetch real-time analytics & subscription quota status for the application
  const { data: analyticsPayload } = useQuery<AnalyticsData | null>({
    queryKey: ['application-overview-analytics', client?.id, application.id],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: AnalyticsData }>(
          `/clients/${client?.id}/applications/${application.id}/analytics`,
          {
            params: { period: '30d' },
          }
        );
        return res.data?.data || null;
      } catch {
        return null;
      }
    },
    enabled: !!client?.id && !!application.id,
    refetchInterval: 30000,
    staleTime: 30000,
  });

  const subscriptions = subsData?.data || [];
  const appSubscriptions = subscriptions.filter(
    (s) => s.application_id === application.id && s.status === 'active'
  );
  const allProducts = productsData?.data || [];

  // Combine subscribed products with explicitly added extra products
  const subscribedProductIds = new Set(appSubscriptions.map((s) => s.product_id));
  const additionalProducts = allProducts.filter(
    (p) => extraProductIds.includes(p.id) && !subscribedProductIds.has(p.id)
  );
  const availableToAdd = allProducts.filter(
    (p) => !subscribedProductIds.has(p.id) && !extraProductIds.includes(p.id)
  );

  const handleAddProduct = () => {
    if (!selectedToAdd) return;
    setExtraProductIds((prev) => [...prev, selectedToAdd]);
    setSelectedToAdd('');
  };

  // Quota Intelligence & Health Metrics
  const quotas: SubscriptionQuotaItem[] = analyticsPayload?.subscriptions_quota || [];
  const summary = analyticsPayload?.summary || {
    total_requests: 0,
    successful_requests: 0,
    client_errors: 0,
    server_errors: 0,
    average_response_time: 0,
    total_cost: 0,
    success_rate: 100,
    error_rate: 0,
  };

  const criticalQuotas = quotas.filter((q) => q.quota_percentage >= 95);
  const warningQuotas = quotas.filter((q) => q.quota_percentage >= 80 && q.quota_percentage < 95);

  const totalAggregatedQuota = quotas.reduce((acc, q) => acc + q.monthly_quota, 0);
  const totalAggregatedUsage = quotas.reduce((acc, q) => acc + q.current_month_usage, 0);
  const aggregatedQuotaPercentage =
    totalAggregatedQuota > 0 ? (totalAggregatedUsage / totalAggregatedQuota) * 100 : 0;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Critical / Warning Quota Exhaustion Alert Banner */}
      {criticalQuotas.length > 0 && (
        <div className="p-4 sm:p-5 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-start gap-3.5">
            <div className="p-2 rounded-xl bg-rose-100 dark:bg-rose-900/60 text-rose-600 dark:text-rose-400 shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-rose-900 dark:text-rose-200 flex items-center gap-2">
                <span>Limite Crítico de Quota Atingido</span>
                <span className="text-2xs font-mono px-2 py-0.5 rounded-full bg-rose-200 dark:bg-rose-900 text-rose-800 dark:text-rose-300 font-bold">
                  HTTP 429 Iminente
                </span>
              </h4>
              <p className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                {criticalQuotas
                  .map((q) => `${q.product_name} (${q.quota_percentage.toFixed(1)}%)`)
                  .join(', ')}{' '}
                ultrapassou 95% da quota mensal. Faça upgrade imediatamente para prevenir bloqueios
                de chamadas.
              </p>
            </div>
          </div>
          <Link
            to={`/applications/${application.id}/subscriptions`}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors shrink-0 flex items-center gap-1.5"
          >
            <span>Fazer Upgrade Agora</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {warningQuotas.length > 0 && criticalQuotas.length === 0 && (
        <div className="p-4 sm:p-5 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-start gap-3.5">
            <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/60 text-amber-600 dark:text-amber-400 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                Aviso Preventivo de Quota (Acima de 80%)
              </h4>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                {warningQuotas
                  .map((q) => `${q.product_name} (${q.quota_percentage.toFixed(1)}%)`)
                  .join(', ')}{' '}
                está a aproximar-se do limite contratado. Considere atualizar o plano para manter
                margem operacional.
              </p>
            </div>
          </div>
          <Link
            to={`/applications/${application.id}/subscriptions`}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors shrink-0 flex items-center gap-1.5"
          >
            <span>Gerir Subscrições</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* Application Pulse & Health KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Subscriptions */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs hover:border-primary-300 dark:hover:border-primary-800 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Subscrições Ativas
            </span>
            <div className="p-2 rounded-xl bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
              {appSubscriptions.length}
            </span>
            <Link
              to={`/applications/${application.id}/subscriptions`}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold flex items-center gap-0.5"
            >
              <span>Ver todas</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <span className="text-2xs text-slate-500 dark:text-slate-400 mt-1 block">
            APIs autorizadas para esta aplicação
          </span>
        </div>

        {/* Total Requests (30d) */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs hover:border-emerald-300 dark:hover:border-emerald-800 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Tráfego (30 dias)
            </span>
            <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
              <Activity className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
              {summary.total_requests.toLocaleString()}
            </span>
            <span className="inline-flex items-center gap-1 text-2xs font-bold font-mono px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
              <CheckCircle2 className="w-3 h-3" />
              <span>{summary.success_rate.toFixed(1)}%</span>
            </span>
          </div>
          <span className="text-2xs text-slate-500 dark:text-slate-400 mt-1 block">
            Taxa de sucesso operacional do Gateway
          </span>
        </div>

        {/* Aggregated Quota Consumption */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs hover:border-sky-300 dark:hover:border-sky-800 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Consumo de Quota
            </span>
            <div className="p-2 rounded-xl bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400">
              <Gauge className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
              {aggregatedQuotaPercentage.toFixed(1)}%
            </span>
            <span className="text-2xs font-mono text-slate-500 dark:text-slate-400">
              {totalAggregatedUsage.toLocaleString()} / {totalAggregatedQuota.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full mt-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                aggregatedQuotaPercentage >= 95
                  ? 'bg-rose-500'
                  : aggregatedQuotaPercentage >= 80
                    ? 'bg-amber-500'
                    : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min(100, aggregatedQuotaPercentage)}%` }}
            />
          </div>
        </div>

        {/* Average Latency */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs hover:border-amber-300 dark:hover:border-amber-800 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Latência Média
            </span>
            <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
              {Math.round(summary.average_response_time)} ms
            </span>
            <Link
              to={`/applications/${application.id}/analytics`}
              className="text-xs text-amber-600 dark:text-amber-400 hover:underline font-semibold flex items-center gap-0.5"
            >
              <span>Analytics</span>
              <TrendingUp className="w-3.5 h-3.5" />
            </Link>
          </div>
          <span className="text-2xs text-slate-500 dark:text-slate-400 mt-1 block">
            Tempo médio de round-trip do Gateway
          </span>
        </div>
      </div>

      {/* Subscription Quotas Health Breakdown (if subscriptions exist) */}
      {quotas.length > 0 && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800/80 pb-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Gauge className="w-5 h-5 text-primary-500" />
                <span>Estado de Quotas e Limites por API</span>
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Acompanhe o consumo mensal e os limites de taxa por minuto (Rate Limits) contratados
              </p>
            </div>
            <Link
              to={`/applications/${application.id}/subscriptions`}
              className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
            >
              <span>Gerir Planos</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quotas.map((item) => {
              const isCrit = item.quota_percentage >= 95;
              const isWarn = item.quota_percentage >= 80 && !isCrit;

              return (
                <div
                  key={item.subscription_id}
                  className={`p-4 rounded-xl border transition-all ${
                    isCrit
                      ? 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/50'
                      : isWarn
                        ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/50'
                        : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200/80 dark:border-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {item.product_name}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-2xs font-bold uppercase tracking-wider bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                        {item.tier}
                      </span>
                    </div>
                    <span
                      className={`text-2xs font-bold font-mono px-2 py-0.5 rounded-full ${
                        isCrit
                          ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300'
                          : isWarn
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300'
                      }`}
                    >
                      {item.quota_percentage.toFixed(1)}% Consumido
                    </span>
                  </div>

                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden my-2.5">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isCrit ? 'bg-rose-500' : isWarn ? 'bg-amber-500' : 'bg-primary-500'
                      }`}
                      style={{ width: `${Math.min(100, item.quota_percentage)}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-2xs text-slate-600 dark:text-slate-400 font-mono mt-1">
                    <span>
                      Uso:{' '}
                      <strong className="text-slate-900 dark:text-slate-200">
                        {item.current_month_usage.toLocaleString()}
                      </strong>{' '}
                      / {item.monthly_quota.toLocaleString()} reqs
                    </span>
                    <span>
                      Limite:{' '}
                      <strong className="text-slate-900 dark:text-slate-200">
                        {item.rate_limit_per_minute}
                      </strong>{' '}
                      req/min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Definições Gerais da Aplicação */}
      <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2.5">
          <Layers className="w-6 h-6 text-primary-500" />
          <span>Definições da Aplicação</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400">
              <Server className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider">
                Ambiente de Execução
              </span>
              <p className="text-sm text-slate-900 dark:text-slate-100 font-bold mt-0.5">
                {application.sandbox_mode ? 'Sandbox (Ambiente de Testes)' : 'Produção'}
              </p>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <span className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold tracking-wider">
                Data de Criação
              </span>
              <p className="text-sm text-slate-900 dark:text-slate-100 font-bold mt-0.5">
                {new Date(application.created_at).toLocaleDateString('pt-PT')}
              </p>
            </div>
          </div>
        </div>

        {/* Diagnostic & Gateway Sync Health Action Banner */}
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-primary-50/60 to-slate-50 dark:from-primary-950/20 dark:to-slate-800/40 border border-primary-100 dark:border-primary-900/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/60 text-primary-600 dark:text-primary-400 shrink-0">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                Diagnóstico & Sincronização com Gateway Kong
              </h4>
              <p className="text-2xs text-slate-600 dark:text-slate-400 mt-0.5">
                Verifique se as credenciais, rotas e permissões ACL da aplicação estão alinhadas no Kong.
              </p>
            </div>
          </div>
          <ApiKeyDiagnosticModal
            applicationId={application.id}
            clientId={client?.id || ''}
            applicationName={application.name}
            triggerButtonVariant="compact"
            customButtonText="Diagnosticar Chave & Gateway"
          />
        </div>
      </div>

      {/* Quick Start & Terminal Code Snippet */}
      <CodeSnippetGenerator
        apiKey={
          apiKeyData
            ? apiKeyData
            : application.api_key_prefix
              ? `${application.api_key_prefix}••••••••••••••••••••`
              : 'mk_test_suaChaveAqui'
        }
        applicationId={application.id}
        gatewayUrl={gatewayUrl}
      />

      {/* Credenciais de Endpoints Externos da Aplicação */}
      <div
        id="credenciais-externas"
        className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm scroll-mt-24"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Key className="w-6 h-6 text-primary-600" /> Credenciais de APIs Externas
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
              Gira as credenciais dos serviços e fornecedores de destino (ex: SIGFE/RUPE, e-Kwanza).
              O Gateway injeta estas credenciais de forma segura nas chamadas feitas por esta
              aplicação.
            </p>
          </div>

          {availableToAdd.length > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              <select
                value={selectedToAdd}
                onChange={(e) => setSelectedToAdd(e.target.value)}
                className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Selecionar API do catálogo...</option>
                {availableToAdd.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddProduct}
                disabled={!selectedToAdd}
                className="btn-base btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
              >
                <PlusCircle className="w-4 h-4" /> Configurar
              </button>
            </div>
          )}
        </div>

        {appSubscriptions.length > 0 || additionalProducts.length > 0 ? (
          <div className="space-y-6">
            {/* Subscribed products */}
            {appSubscriptions.map((sub) => (
              <div
                key={sub.id}
                className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/60"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-slate-900 dark:text-white text-base">
                    {sub.product?.name || 'Produto'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-semibold">
                    Subscrição Ativa
                  </span>
                </div>
                <AppCredentialsManager
                  applicationId={application.id}
                  clientId={client?.id || ''}
                  productId={sub.product_id}
                  authProvider={sub.product?.authentication_provider || 'BASIC_AUTH'}
                  productName={sub.product?.name}
                />
              </div>
            ))}

            {/* Manually added catalog products */}
            {additionalProducts.map((product) => (
              <div
                key={product.id}
                className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/60"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-slate-900 dark:text-white text-base">
                    {product.name}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 font-semibold">
                    Catálogo
                  </span>
                </div>
                <AppCredentialsManager
                  applicationId={application.id}
                  clientId={client?.id || ''}
                  productId={product.id}
                  authProvider={product.authentication_provider || 'BASIC_AUTH'}
                  productName={product.name}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
            <Shield className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
              Nenhuma credencial configurada
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Subscreva uma API no catálogo ou selecione uma API no seletor acima para configurar as
              credenciais externas desta aplicação.
            </p>
          </div>
        )}
      </div>

      {/* Origens CORS Autorizadas */}
      <div
        id="origens-cors"
        className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm scroll-mt-24"
      >
        <CorsManager
          applicationId={application.id}
          clientId={client?.id || ''}
          currentOrigins={application.cors_allowed_origins || []}
          isSandbox={application.sandbox_mode}
        />
      </div>

      {/* Alertas de Quota & Notificações Proativas */}
      <div id="quota-alerts">
        <QuotaAlertSettings
          applicationId={application.id}
          clientId={client?.id || ''}
          applicationName={application.name}
        />
      </div>

      {/* Webhooks da Aplicação */}
      <div
        id="webhooks"
        className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm scroll-mt-24"
      >
        <WebhookManager applicationId={application.id} clientId={client?.id || ''} />
      </div>
    </div>
  );
};
